<script setup lang="ts">
//#region import
import { ref, computed } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { isErrorResponse, typeGuard, type EmitsToListeners, type ErrorResponse, type FormErrors, type ToErrorMap } from '@/types/core';
import type { PostControlTowerCustomerCurrenciesData } from '@/types/openapi-ts';
//#endregion

//#region types
type PostControlTowerCustomerCurrenciesErrors = ToErrorMap<PostControlTowerCustomerCurrenciesData['body']>;

interface Props {
  excluded?: string[]
}

interface  Emits {
  'close': [],
  'success': []
};

export interface AddCurrencyParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  excluded: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const store = useTypedStore();
const api = useApi();
const isVisible = ref(false);
const errors = ref<PostControlTowerCustomerCurrenciesErrors>({});
const currency_code = ref('');
//#endregion

//#region computed
const currencies = computed(() => store.getters['misc/currencies']);
const filterCurrency = computed(() => {
  return currencies.value.filter((currency) => !props.excluded.includes(currency.code));
});
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};
const handleSave = () => {
  errors.value = {};
  api.postCustomerCurrencies({ currency_code: currency_code.value }).then(() => {
    store.dispatch('misc/MISC_GET_CUSTOMER_CURRENCIES');
    emit('success');
    handleClose();
  }).catch((e) => {
    if (typeGuard<ErrorResponse<PostControlTowerCustomerCurrenciesErrors>>(e)) {
      errors.value = e?.data?.errors ? cloneDeep(e.data.errors) : {};
    }
  });
};
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-currency-popup"
    :title="$t('settings.currencies.addCurrency')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <UiSelectElement
        v-model="currency_code"
        :placeholder="$t('settings.currencies.confirm.currency')"
        :label="$t('settings.currencies.confirm.currency')"
        :list="filterCurrency"
        option-value-param="code"
        filterable
        size="big"
        :error="errors.currency_code"
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
